import { useState, useMemo, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Calendar, MapPin, Search, Filter, ArrowLeft, ArrowRight, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useActiveAds } from "@/hooks/useActiveAds";
import { allPartiesIncludingNonParliamentary, moroccoRegions } from "@/data/parties";
import { PartyEvent } from "@/components/PartiesEnActionSection";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { isStagingModeEnabled } from "@/utils/stagingMode";
import { fetchPublicEventsFromServer, PendingEvent } from "@/data/eventsStore";

const typeColors: Record<string, string> = {
  conference: "bg-primary/10 text-primary border-primary/20",
  meeting: "bg-accent/10 text-accent-foreground border-accent/20",
  formation: "bg-secondary/10 text-secondary border-secondary/20",
  campagne: "bg-destructive/10 text-destructive border-destructive/20",
  autre: "bg-muted text-muted-foreground border-border",
};

const typeLabels: Record<string, Record<string, string>> = {
  fr: { conference: "Conférence", meeting: "Réunion", formation: "Formation", campagne: "Campagne", autre: "Autre" },
  ar: { conference: "مؤتمر", meeting: "اجتماع", formation: "تكوين", campagne: "حملة", autre: "أخرى" },
  zgh: { conference: "ⴰⴳⵔⴰⵡ", meeting: "ⴰⵎⵙⴰⵡⴰⵍ", formation: "ⴰⵙⵎⵖⵔ", campagne: "ⵜⴰⵎⵀⵍⴰ", autre: "ⵢⴰⴹⵏ" },
};

const ITEMS_PER_PAGE = 12;

const AllEventsPage = () => {
  const { t, lang, isRtl } = useI18n();
  const { type } = useParams<{ type: string }>(); // 'partis' ou 'citoyens'
  const isPartis = type === 'partis';

  const [searchParams] = useSearchParams();
  const urlRegion = searchParams.get("region") || "all";

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedParty, setSelectedParty] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState(urlRegion);
  const [selectedProvince, setSelectedProvince] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [localEvents, setLocalEvents] = useState<any[]>([]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const parsed = await fetchPublicEventsFromServer();
        const isStaging = isStagingModeEnabled();
        // Le backend filtre déjà status === "approved" et (publishStatus !== 'draft') 
        // Mais en mode Staging, on pourrait vouloir voir les drafts si l'API les renvoyait,
        // ce qui nécessiterait une auth admin. L'API publique ne renvoie pas les drafts.
        // Donc on affiche simplement ce qu'elle renvoie.
        const approvedAndFormatted = parsed.map((e: any) => ({
          id: e.id,
          partyAcronym: e.partyAcronym,
          title: e.title,
          description: e.description,
          date: e.date,
          location: e.location,
          type: e.type,
          imageUrl: e.imageUrl,
          organizerStatus: e.organizerStatus || 'parti',
          status: "upcoming" as const,
          registrationType: e.registrationType || "public",
        }));
        setLocalEvents(approvedAndFormatted);
      } catch (err) {}
    };
    loadEvents();
    window.addEventListener("stagingModeChanged", loadEvents);
    return () => {
      window.removeEventListener("stagingModeChanged", loadEvents);
    }
  }, []);

  const allAvailableEvents = useMemo(() => localEvents, [localEvents]);

  // Filtrer uniquement selon le contexte de la page
  const sectionEvents = useMemo(() => {
    return allAvailableEvents.filter(e => {
        if (isPartis) return e.organizerStatus === 'parti';
        return ['ong', 'citoyen', 'institution'].includes(e.organizerStatus);
    });
  }, [allAvailableEvents, isPartis]);

  const uniqueParties = useMemo(() => Array.from(new Set(sectionEvents.map(e => e.partyAcronym))), [sectionEvents]);

  const filteredEvents = useMemo(() => {
    return sectionEvents.filter(e => {
      const matchesSearch = e.title.toLowerCase().includes(searchTerm.toLowerCase()) || e.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesParty = selectedParty === "all" || e.partyAcronym === selectedParty;
      const matchesRegion = selectedRegion === "all" || e.location?.includes(selectedRegion);
      const matchesProvince = selectedProvince === "all" || e.location?.includes(selectedProvince);
      const matchesFilterType = selectedType === "all" || e.type === selectedType;
      
      return matchesSearch && matchesParty && matchesRegion && matchesProvince && matchesFilterType;
    });
  }, [sectionEvents, searchTerm, selectedParty, selectedRegion, selectedProvince, selectedType]);

  const currentProvinces = useMemo(() => {
    if (selectedRegion === "all") return [];
    return moroccoRegions.find(r => r.name === selectedRegion)?.provinces || [];
  }, [selectedRegion]);

  const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);
  const paginatedEvents = filteredEvents.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const sectionLabels = {
    fr: { 
      titlePartis: "Moteur de recherche des événements des partis", 
      titleCitoyens: "Moteur de recherche citoyen & institutionnel",
      allOrganizers: isPartis ? "Tous les partis" : "Tous les organisateurs", 
      allRegions: "Toutes les régions", 
      allProvinces: "Toutes les provinces", 
      searchPlaceholder: "Rechercher...", 
      filterType: "Catégorie", 
      searchBtn: "Rechercher", 
      noResults: "Aucun événement.",
      all: "Toutes les catégories",
      backHome: "Retour à l'accueil",
    },
    // Trads omises pour concision.
    ar: { 
      titlePartis: "محرك بحث فعاليات الأحزاب", 
      titleCitoyens: "محرك بحث الفعاليات المواطنة والمؤسساتية",
      allOrganizers: isPartis ? "جميع الأحزاب" : "جميع المنظمين", 
      allRegions: "جميع الجهات", 
      allProvinces: "جميع الأقاليم", 
      searchPlaceholder: "بحث...", 
      filterType: "Фئـة", 
      searchBtn: "بحث", 
      noResults: "لا توجد فعاليات.",
      all: "جميع الفئات",
      backHome: "العودة للرئيسية",
    },
    zgh: {
      titlePartis: "ⴰⵔⵣⵣⵓ ⵏ ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵉⵎⵓⵔⴰⵏ",
      titleCitoyens: "ⴰⵔⵣⵣⵓ ⵏ ⵉⵎⵓⵙⵙⵓⵜⵏ ⵉⵏⴰⵎⵓⵔⵏ",
      allOrganizers: "ⵎⴰⵕⵕⴰ", allRegions: "ⴰⴽⴽⵯ ⵜⵉⵎⵏⴰⴹⵉⵏ", allProvinces: "ⴰⴽⴽⵯ ⵜⵉⵙⴳⴰⵡⵉⵏ", searchPlaceholder: "ⵔⵣⵓ...", filterType: "ⴰⵏⴰⵡ", searchBtn: "ⵔⵣⵓ", noResults: "ⵓⵔ ⵉⵍⵍⵉ.", all: "ⴰⴽⴽⵯ ⵜⵉⴳⴳⴰⵢⵉⵏ", backHome: "ⵓⵖⴰⵍ ⵙ ⵓⵙⵏⵓⴱⴳ"
    }
  };

  const labels = sectionLabels[lang as keyof typeof sectionLabels] || sectionLabels.fr;

  const formatDateBox = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { m: "", d: "" };
    const m = d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'ar' ? 'ar-MA' : 'en-US', { month: 'short' });
    const day = d.getDate();
    return { m: m.toUpperCase(), d: day.toString() };
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/20">
      <Navbar />
      <main className="flex-1 container py-24">
        
        <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors" asChild>
           <Link to="/accueil-evenements">
             <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />
             {labels.backHome}
           </Link>
        </Button>

        <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-display font-bold">
                {isPartis ? labels.titlePartis : labels.titleCitoyens}
            </h1>
        </div>

        {/* Moteur de recherche avancé (similaire à l'ancien accueil) */}
        <form onSubmit={(e) => e.preventDefault()} className="bg-background border border-border shadow-md rounded-2xl p-4 mb-10 mx-auto grid grid-cols-2 lg:flex lg:flex-row items-center gap-3">
          <div className="relative col-span-2 lg:col-span-1 lg:flex-1 w-full">
            <Search className={`absolute ${isRtl ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
            <Input 
              placeholder={labels.searchPlaceholder} 
              value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className={`w-full ${isRtl ? 'pr-9 pl-3' : 'pl-9 pr-3'} border-0 bg-muted focus-visible:ring-1`}
            />
          </div>
          <div className="hidden lg:block w-px h-8 bg-border" />
          <Select value={selectedParty} onValueChange={(v) => { setSelectedParty(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-full lg:w-52 border-0 bg-transparent focus:ring-0">
              <SelectValue placeholder={labels.allOrganizers} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="font-bold">{labels.allOrganizers}</SelectItem>
              {uniqueParties.map((p: any) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="hidden lg:block w-px h-8 bg-border" />
          <Select value={selectedRegion} onValueChange={(val) => { setSelectedRegion(val); setSelectedProvince("all"); setCurrentPage(1); }}>
            <SelectTrigger className="w-full lg:w-48 border-0 bg-transparent focus:ring-0">
              <SelectValue placeholder={labels.allRegions} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="font-bold">{labels.allRegions}</SelectItem>
              {moroccoRegions.map(r => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {currentProvinces.length > 0 && (
            <>
              <div className="hidden lg:block w-px h-8 bg-border" />
              <Select value={selectedProvince} onValueChange={(v) => { setSelectedProvince(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full lg:w-48 border-0 bg-transparent focus:ring-0">
                  <SelectValue placeholder={labels.allProvinces} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="font-bold">{labels.allProvinces}</SelectItem>
                  {currentProvinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          <div className="hidden lg:block w-px h-8 bg-border" />
          <Select value={selectedType} onValueChange={(v) => { setSelectedType(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-full lg:w-48 border-0 bg-transparent focus:ring-0">
              <SelectValue placeholder={labels.filterType} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="font-bold">{labels.all}</SelectItem>
              {["conference", "meeting", "formation", "campagne"].map(t => (
                 <SelectItem key={t} value={t}>{typeLabels[lang][t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </form>

        <div className="w-full min-h-[50vh]">
          {paginatedEvents.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground font-body bg-background rounded-2xl border border-dashed">
                <Filter className="w-12 h-12 mx-auto mb-4 opacity-50" />
                {labels.noResults}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {paginatedEvents.map((event: any) => {
                  const dateObj = formatDateBox(event.date);
                  return (
                    <Link key={event.id} to={`/evenement/${event.id}`} className="group bg-background rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-border flex flex-col">
                      <div className="w-full h-40 bg-muted relative overflow-hidden">
                        {event.imageUrl ? (
                          <img src={event.imageUrl} alt={event.title} className="w-full h-full object-contain bg-white transition-transform duration-700 group-hover:scale-105" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-transparent">
                              <Calendar className="w-10 h-10 text-primary/20" />
                          </div>
                        )}
                        {dateObj.d && (
                          <div className="absolute top-2 left-2 bg-background/95 backdrop-blur-sm shadow-md rounded-lg flex flex-col items-center justify-center min-w-[45px] p-1.5">
                              <span className="text-destructive font-bold text-[10px] leading-none mb-1">{dateObj.m}</span>
                              <span className="text-foreground font-display font-black text-lg leading-none">{dateObj.d}</span>
                          </div>
                        )}
                        <Badge className={`absolute bottom-2 right-2 text-[10px] uppercase ${typeColors[event.type].replace('bg-', 'bg-background/90 text-').replace('text-', 'text-')}`}>
                          {typeLabels[lang][event.type]}
                        </Badge>
                      </div>
                      <div className="flex flex-col flex-1 p-3">
                        <h3 className="font-display font-bold text-sm md:text-base leading-tight mb-2 line-clamp-2">{event.title}</h3>
                        <p className="text-muted-foreground text-xs mb-3 truncate">{event.location}</p>
                        <div className="mt-auto pt-2 border-t border-border flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-[10px] uppercase font-bold text-muted-foreground">
                            {isPartis ? allPartiesIncludingNonParliamentary.find((p: any) => p.acronym === event.partyAcronym)?.symbol || event.partyAcronym.slice(0, 2) : event.partyAcronym.slice(0, 2) }
                          </span>
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase truncate">
                            {event.partyAcronym}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
              <div className="mt-12 flex justify-center items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                      <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex gap-1">
                      {Array.from({ length: totalPages }).map((_, i) => (
                          <Button key={i} variant={currentPage === i + 1 ? "default" : "outline"} size="icon" className="w-8 h-8 rounded-full" onClick={() => setCurrentPage(i + 1)}>
                              {i + 1}
                          </Button>
                      ))}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                      <ArrowRight className="w-4 h-4" />
                  </Button>
              </div>
          )}
          
          <div className="mt-12 flex justify-center">
             <Button variant="ghost" className="font-body hover:bg-transparent hover:text-primary transition-colors" asChild>
               <Link to="/accueil-evenements">
                 <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />
                 {labels.backHome}
               </Link>
             </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AllEventsPage;
